import useTeacherCommunity from '../../../hooks/Marketing/CommunityForvet/useTeacherCommunity';
import './TeacherCommunity.css';

const pct = (n) => (n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`);

function TeacherCard({ teacher, position }) {
    const medals = ['🥇', '🥈', '🥉'];

    return (
        <article className="TeacherCommunity-card">
            <header className="TeacherCommunity-cardHeader">
                <span className="TeacherCommunity-medal">{medals[position] || '•'}</span>
                {teacher.profilePicture && (
                    <img
                        className="TeacherCommunity-avatar"
                        src={teacher.profilePicture}
                        alt={teacher.fullName}
                    />
                )}
                <div>
                    <h4 className="TeacherCommunity-name">{teacher.fullName}</h4>
                    {teacher.specialty && (
                        <span className="TeacherCommunity-specialty">{teacher.specialty}</span>
                    )}
                </div>
            </header>

            <div className="TeacherCommunity-metrics">
                <div className="TeacherCommunity-metric">
                    <strong>{pct(teacher.satisfaction)}</strong>
                    <span>Satisfacción<br /><small>{teacher.votes} votos</small></span>
                </div>
                <div className="TeacherCommunity-metric">
                    <strong>{teacher.lessonsCount}</strong>
                    <span>Clases</span>
                </div>
                <div className="TeacherCommunity-metric">
                    <strong>{teacher.uniqueStudents}</strong>
                    <span>Alumnos<br /><small>alcanzados</small></span>
                </div>
                <div className="TeacherCommunity-metric">
                    <strong>{teacher.countries?.total ?? 0}</strong>
                    <span>Países</span>
                </div>
            </div>

            <div className="TeacherCommunity-breakdown">
                <span className="is-good">👍 {teacher.valorations.good}</span>
                <span className="is-regular">😐 {teacher.valorations.regular}</span>
                <span className="is-bad">👎 {teacher.valorations.bad}</span>
            </div>

            {!!teacher.countries?.list?.length && (
                <p className="TeacherCommunity-countries">
                    {teacher.countries.list
                        .slice(0, 6)
                        .map((c) => `${c.country} (${c.count})`)
                        .join(' · ')}
                    {teacher.countries.unknown > 0 && (
                        <em> · {teacher.countries.unknown} sin país cargado</em>
                    )}
                </p>
            )}

            {!!teacher.academicUnits?.length && (
                <p className="TeacherCommunity-units">{teacher.academicUnits.join(' · ')}</p>
            )}

            {!!teacher.survey?.comments?.length && (
                <details className="TeacherCommunity-comments">
                    <summary>Comentarios de encuesta ({teacher.survey.comments.length})</summary>
                    <p className="TeacherCommunity-commentsWarning">
                        Son del curso completo, no sólo de este mes. Revisalos antes de pegarlos:
                        pueden identificar a quien los escribió.
                    </p>
                    <ul>
                        {teacher.survey.comments.map((c, i) => (
                            <li key={i}>“{c}”</li>
                        ))}
                    </ul>
                </details>
            )}
        </article>
    );
}

export default function TeacherCommunity() {
    const {
        isLoading,
        result,
        message,
        wasEdited,
        copied,
        selectedPeriodValue,
        monthOptions,
        changePeriod,
        generate,
        editMessage,
        restoreMessage,
        copyMessage,
        hasWarnings,
    } = useTeacherCommunity();

    return (
        <section className="TeacherCommunity-container">
            <header className="TeacherCommunity-header">
                <div>
                    <h3>Comunidad Docente</h3>
                    <p>Genera el anuncio de docentes destacados para enviar por WhatsApp.</p>
                </div>

                <div className="TeacherCommunity-controls">
                    <select
                        value={selectedPeriodValue}
                        onChange={(e) => changePeriod(e.target.value)}
                        disabled={isLoading}
                    >
                        {monthOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    <button
                        type="button"
                        className="TeacherCommunity-primaryBtn"
                        onClick={generate}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Calculando…' : 'Generar anuncio'}
                    </button>
                </div>
            </header>

            {isLoading && (
                <p className="TeacherCommunity-loading">
                    Recorriendo clases, valoraciones y países del período…
                </p>
            )}

            {!isLoading && !result && (
                <p className="TeacherCommunity-empty">
                    Elegí un mes y generá el anuncio. El cálculo recorre todas las clases
                    del período, puede tardar unos segundos.
                </p>
            )}

            {result && !isLoading && (
                <>
                    {!!result.ranked?.length && (
                        <div className="TeacherCommunity-grid">
                            {result.ranked.map((t, i) => (
                                <TeacherCard key={t.teacherId} teacher={t} position={i} />
                            ))}
                        </div>
                    )}

                    <div className="TeacherCommunity-messageBox">
                        <div className="TeacherCommunity-messageHeader">
                            <h4>Mensaje para WhatsApp</h4>
                            <div>
                                {wasEdited && (
                                    <button type="button" onClick={restoreMessage}>
                                        Restaurar original
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="TeacherCommunity-primaryBtn"
                                    onClick={() => copyMessage()}
                                    disabled={!message}
                                >
                                    {copied ? '✓ Copiado' : 'Copiar'}
                                </button>
                            </div>
                        </div>

                        <textarea
                            className="TeacherCommunity-textarea"
                            value={message}
                            onChange={(e) => editMessage(e.target.value)}
                            rows={18}
                            spellCheck={false}
                        />

                        <small className="TeacherCommunity-hint">
                            WhatsApp usa *negrita* y _cursiva_, no markdown. Podés editar el texto
                            antes de copiarlo.
                        </small>
                    </div>

                    {hasWarnings && (
                        <details className="TeacherCommunity-warnings">
                            <summary>Revisar antes de enviar</summary>

                            {!!result.excluded?.length && (
                                <p>
                                    <strong>{result.excluded.length}</strong> docente(s) quedaron fuera
                                    por no alcanzar el mínimo de clases o de votos:{' '}
                                    {result.excluded.map((t) => t.fullName).join(', ')}.
                                </p>
                            )}

                            {!!result.warnings?.unmatchedReviewNames?.length && (
                                <p>
                                    Nombres en encuestas que no se pudieron asociar a ningún docente:{' '}
                                    {result.warnings.unmatchedReviewNames.join(', ')}. Sus valoraciones
                                    no entraron en el cálculo.
                                </p>
                            )}

                            {!!result.warnings?.countriesWithoutData && (
                                <p>
                                    <strong>{result.warnings.countriesWithoutData}</strong> alumno(s) sin
                                    país cargado. El conteo de países los deja afuera.
                                </p>
                            )}

                            {result.warnings?.note && <p>{result.warnings.note}</p>}
                        </details>
                    )}
                </>
            )}
        </section>
    );
}